import { AHSeparator, useTheme } from "arkh";
import {
  graphql,
  PreloadedQuery,
  usePreloadedQuery,
  useQueryLoader,
} from "react-relay";
import { FlatList } from "react-native";
import React, { useCallback, useMemo, useTransition } from "react";
import { WorkspaceProductsSearchQuery } from "./__generated__/WorkspaceProductsSearchQuery.graphql";
import { WorkspaceProductSearchListItem } from "./WorkspaceProductSearchItem";
import { useDebouncedCallback } from "use-debounce";
import {
  ProductSearchInitialEmptyState,
  ProductSearchNoResults,
} from "../../Products/ProductSearch";
import { SearchInput } from "../../Products/SearchInput";

const PRODUCT_SEARCH_QUERY = graphql`
  query WorkspaceProductsSearchQuery($workspaceId: ID!, $query: String!) {
    workspace(id: $workspaceId) @required(action: THROW) {
      productSearch(query: $query) {
        hits {
          id
          product {
            id
          }
          ...WorkspaceProductSearchItem_workspaceProductSearchHit
        }
      }
    }
  }
`;

export function WorkspaceProductsSearch(props: {
  onPressProduct: (id: string) => void;
  productIsDisabled?: (id: string) => string | undefined;
  onFocus?: () => void;
  onBlur?: () => void;
  isSearching: boolean;
  workspaceId: string;
  autoFocus?: boolean;
  showCancel?: boolean;
}) {
  const [query, setQuery] = React.useState("");

  const [, startTransition] = useTransition();

  const [queryRef, loadQuery] =
    useQueryLoader<WorkspaceProductsSearchQuery>(PRODUCT_SEARCH_QUERY);

  const { workspaceId } = props;
  const search = useCallback(
    (query: string) => {
      loadQuery({ query, workspaceId });
    },
    [loadQuery, workspaceId]
  );

  const debouncedSearch = useDebouncedCallback((fn: string) => {
    startTransition(() => {
      search(fn);
    });
  }, 500);

  return (
    <>
      <SearchInput
        query={query}
        onChange={(e) => {
          setQuery(e.nativeEvent.text);
          debouncedSearch(e.nativeEvent.text);
        }}
        onFocus={props.onFocus}
        onBlur={props.onBlur}
        isSearching={props.isSearching}
        autoFocus={props.autoFocus}
        showCancel={props.showCancel}
      />
      {props.isSearching ? (
        query !== "" && queryRef !== null && queryRef !== undefined ? (
          <WorkspaceProductsSearchComponent
            queryRef={queryRef}
            onPressProduct={props.onPressProduct}
            productIsDisabled={props.productIsDisabled}
          />
        ) : (
          <ProductSearchInitialEmptyState />
        )
      ) : null}
    </>
  );
}
function WorkspaceProductsSearchComponent(props: {
  queryRef: PreloadedQuery<WorkspaceProductsSearchQuery>;
  onPressProduct: (id: string) => void;
  productIsDisabled?: (id: string) => string | undefined;
}) {
  const data = usePreloadedQuery(PRODUCT_SEARCH_QUERY, props.queryRef);
  const theme = useTheme();
  const searchResult = useMemo(() => {
    return data?.workspace.productSearch?.hits ?? [];
  }, [data?.workspace.productSearch.hits]);

  const renderItem = useCallback(
    ({
      item,
      index,
    }: {
      item: (typeof searchResult)[number];
      index: number;
    }) => {
      const disabledReason = props.productIsDisabled?.(item.product.id ?? "");
      return (
        <WorkspaceProductSearchListItem
          isLast={
            (data.workspace.productSearch?.hits.length ?? 0) - 1 === index
          }
          onPressProduct={props.onPressProduct}
          product={item}
          disabled={disabledReason !== undefined}
          disabledReason={disabledReason}
        />
      );
    },
    [data.workspace.productSearch?.hits.length, props]
  );

  return (
    <FlatList
      ListEmptyComponent={<ProductSearchNoResults />}
      keyboardShouldPersistTaps="never"
      style={{
        backgroundColor: theme.surface,
        flex: 1,
      }}
      ItemSeparatorComponent={AHSeparator}
      data={searchResult}
      keyExtractor={(item, index) => item.id ?? index + ""}
      renderItem={renderItem}
    />
  );
}
